import { useCallback, useEffect, useState } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useTilePersistence, type TileLayout } from '../hooks/useTilePersistence';
import { useTileData } from '../hooks/useTileData';
import { DashboardToolbar } from '../components/DashboardToolbar';
import { DashboardGrid } from '../components/DashboardGrid';
import { TileEditor } from '../components/TileEditor';
import { DateRangePicker } from '../components/DateRangePicker';
import type { Tile } from '../components/TileDisplay';

const toDateRange = (startDate: number | null, endDate: number | null) => ({
  ...(startDate !== null && { start: startDate }),
  ...(endDate !== null && { end: endDate }),
});

/**
 * Dashboard page: orchestrates tile layout, data fetching, and persistence.
 *
 * Architecture:
 * - TileLayout (persisted): { id, title, dataSource, chartType }
 *   → Saved to backend via useTilePersistence
 * - Tile (ephemeral): TileLayout + { data: [...] }
 *   → Fetched via useTileData, not persisted
 *
 * This separation means:
 * - Layout changes persist across sessions
 * - Data is always fresh on page load
 * - DnD reorders are saved immediately
 */
export function DashboardPage() {
  // Layout persistence (what tiles exist, in what order)
  const tilePersist = useTilePersistence();

  // Data fetching (filling layouts with actual data)
  const tileData = useTileData();

  // UI state
  const [layouts, setLayouts] = useState<TileLayout[]>([]);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [showAddTile, setShowAddTile] = useState(false);
  const [showPickDate, setShowPickDate] = useState(false);
  const [startDate, setStartDate] = useState<number | null>(null);
  const [endDate, setEndDate] = useState<number | null>(null);
  const [viewLatestData, setViewLatestData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initial load on mount only.
   */
  useEffect(() => {
    const loadDashboard = async () => {
      setError(null);

      try {
        // 1. Load layouts from backend
        const savedLayouts = await tilePersist.load();
        setLayouts(savedLayouts);

        // 2. Fetch data for those layouts
        if (savedLayouts.length > 0) {
          const tilesWithData = await tileData.fetchTileData(savedLayouts);
          setTiles(tilesWithData);
        }
      } catch (err) {
        setError('Failed to load dashboard. Please refresh the page.');
        console.error('Dashboard load error:', err);
      }
    };

    loadDashboard();
  }, []);

  /**
   * Retry loading dashboard on error.
   */
  const handleRetry = useCallback(async () => {
    setError(null);

    try {
      const savedLayouts = await tilePersist.load();
      setLayouts(savedLayouts);

      if (savedLayouts.length > 0) {
        const params = startDate !== null || endDate !== null ? toDateRange(startDate, endDate) : undefined;
        const tilesWithData = await tileData.fetchTileData(savedLayouts, params);
        setTiles(tilesWithData);
      }
    } catch (err) {
      setError('Failed to load dashboard. Please refresh the page.');
      console.error('Dashboard load error:', err);
    }
  }, [tilePersist, tileData, startDate, endDate]);

  /**
   * Add a new tile: create layout, fetch data, save immediately.
   */
  const handleAddTile = useCallback(
    async (newLayout: Omit<TileLayout, 'id'>) => {
      try {
        const layoutWithId: TileLayout = {
          ...newLayout,
          id: `tile-${Date.now()}`,
        };

        // Fetch data for the new tile
        const [tileWithData] = await tileData.fetchTileData(
          [layoutWithId],
          toDateRange(startDate, endDate)
        );

        // Update state
        const updatedLayouts = [...layouts, layoutWithId];
        setLayouts(updatedLayouts);
        setTiles([...tiles, tileWithData]);

        // Persist immediately
        setShowAddTile(false);
        await tilePersist.save(updatedLayouts);
      } catch (err) {
        setError('Failed to add tile. Please try again.');
        console.error('Add tile error:', err);
      }
    },
    [layouts, tiles, tileData, tilePersist, startDate, endDate]
  );

  /**
   * Reorder tiles and save layout immediately.
   */
  const handleReorderTiles = useCallback(
    async (reorderedTiles: Tile[]) => {
      const reorderedLayouts = reorderedTiles.map(({ id, title, dataSource, chartType }) => ({
        id,
        title,
        dataSource,
        chartType,
      }));

      setTiles(reorderedTiles);
      setLayouts(reorderedLayouts);

      // Auto-save layout on reorder
      await tilePersist.save(reorderedLayouts);
    },
    [tilePersist]
  );

  /**
   * Apply date range and refresh data.
   */
  const handleApplyDateRange = useCallback(
    async (start: number | null, end: number | null) => {
      setStartDate(start);
      setEndDate(end);
      setViewLatestData(false);

      // Refetch tile data with new date range
      if (layouts.length > 0) {
        const tilesWithData = await tileData.fetchTileData(layouts, toDateRange(start, end));
        setTiles(tilesWithData);
      }

      setShowPickDate(false);
    },
    [layouts, tileData]
  );

  /**
   * Clear date range and refetch latest data.
   */
  const handleClearDateRange = useCallback(async () => {
    setStartDate(null);
    setEndDate(null);
    setViewLatestData(true);

    if (layouts.length > 0) {
      const tilesWithData = await tileData.fetchTileData(layouts);
      setTiles(tilesWithData);
    }
  }, [layouts, tileData]);

  /**
   * Refresh data (manual refresh button).
   */
  const handleRefreshData = useCallback(async () => {
    if (layouts.length > 0) {
      const tilesWithData = await tileData.fetchTileData(layouts, toDateRange(startDate, endDate));
      setTiles(tilesWithData);
    }
  }, [layouts, tileData, startDate, endDate]);

  useEffect(() => {
    if (!viewLatestData) {
      return;
    }

    const intervalId = window.setInterval(() => {
      handleRefreshData();
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [viewLatestData, handleRefreshData]);

  const isLoading = tilePersist.loading;
  const isLoadingData = tileData.loading;
  const isSaving = tilePersist.saving;
  const hasError = tilePersist.error || tileData.error || error;

  if (isLoading) {
    return (
      <>
        <div className="mb-4 d-flex justify-content-start">
          <h1>Dashboard</h1>
        </div>
        <Container className="mt-5 text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className="mb-4 d-flex justify-content-start">
          <h1>Dashboard</h1>
        </div>
        <Container className="mt-5">
          <div className="alert alert-danger">
            <h4>Error</h4>
            <p>{hasError}</p>
            <button className="btn btn-primary" onClick={handleRetry}>
              Retry
            </button>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <div className="mb-4 d-flex justify-content-start">
        <h1>Dashboard</h1>
      </div>

      <DashboardToolbar
        onAddTile={() => setShowAddTile(true)}
        onSave={() => tilePersist.save(layouts)}
        onPickDateRange={() => setShowPickDate(true)}
        onRefreshData={handleRefreshData}
        onClearDateRange={handleClearDateRange}
        hasAnyTiles={tiles.length > 0}
        startDate={startDate}
        endDate={endDate}
        isLoading={isLoading}
        isSaving={isSaving}
        isLoadingData={isLoadingData}
      />

      {hasError && (
        <Container>
          <div className="alert alert-danger mx-3 mb-3">
            <p className="mb-0">{hasError}</p>
          </div>
        </Container>
      )}

      <DashboardGrid tiles={tiles} onTilesReorder={handleReorderTiles} isLoading={isLoadingData} />

      <TileEditor
        show={showAddTile}
        onClose={() => setShowAddTile(false)}
        onAdd={handleAddTile}
        isLoading={isLoadingData}
      />

      <DateRangePicker
        show={showPickDate}
        onClose={() => setShowPickDate(false)}
        onApply={handleApplyDateRange}
        isLoading={isLoadingData}
      />
    </>
  );
}